/**
 * Unified Insurance Search Hook
 *
 * Searches both InsurancePlans and Organizations (insurers) simultaneously,
 * returning results grouped by Organization for hierarchical display.
 *
 * Uses FHIR _include and _revinclude for efficient server-side fetching.
 */

import { useState, useEffect, useCallback } from "react";
import { useMedplum } from "@medplum/react";
import type {
  Bundle,
  Organization,
  InsurancePlan,
  Resource,
} from "@medplum/fhirtypes";
import { useDebounce } from "@benchmark/ui/hooks";

/** Insurance search result: organization with its plans. @agentic @benchmark/core */
export type InsuranceSearchResultGroup = {
  organization: Organization;
  plans: InsurancePlan[];
};

/** Result from useUnifiedInsuranceSearch. @agentic @benchmark/core */
export type UseUnifiedInsuranceSearchResult = {
  data: InsuranceSearchResultGroup[];
  isLoading: boolean;
  error: Error | null;
  clearData: () => void;
};

/**
 * Extracts resources from a FHIR Bundle by resourceType.
 * @agentic @benchmark/core
 */
const extractFromBundle = <T extends Resource>(
  bundle: Bundle | undefined,
  resourceType: string,
): T[] => {
  if (!bundle?.entry) return [];
  return bundle.entry
    .map((entry) => entry.resource)
    .filter((r): r is T => r?.resourceType === resourceType);
};

/**
 * Groups InsurancePlans by their owning Organization.
 * @agentic @benchmark/core
 */
const groupByOrganization = (
  organizations: Organization[],
  plans: InsurancePlan[],
): InsuranceSearchResultGroup[] => {
  // Create a map of organization ID to organization
  const orgMap = new Map<string, Organization>();
  organizations.forEach((org) => {
    if (org.id) orgMap.set(org.id, org);
  });

  // Create a map of organization ID to plans
  const plansByOrgId = new Map<string, InsurancePlan[]>();
  plans.forEach((plan) => {
    const orgRef = plan.ownedBy?.reference;
    if (orgRef) {
      const orgId = orgRef.replace("Organization/", "");
      const existing = plansByOrgId.get(orgId) || [];
      existing.push(plan);
      plansByOrgId.set(orgId, existing);

      // If we don't have the org yet (plan matched but org didn't),
      // we'll need to use info from the _include
      if (!orgMap.has(orgId) && plan.ownedBy) {
        // Create placeholder org from reference - will be filled by _include
        orgMap.set(orgId, {
          resourceType: "Organization",
          id: orgId,
          name: plan.ownedBy.display || `Organization ${orgId}`,
        });
      }
    }
  });

  // Build grouped results, sorted by organization name
  const groups: InsuranceSearchResultGroup[] = [];
  orgMap.forEach((org) => {
    groups.push({
      organization: org,
      plans: (plansByOrgId.get(org.id!) || []).sort((a, b) =>
        (a.name || "").localeCompare(b.name || ""),
      ),
    });
  });

  return groups.sort((a, b) =>
    (a.organization.name || "").localeCompare(b.organization.name || ""),
  );
};

/**
 * Search InsurancePlans and Organizations (insurers) with grouped results.
 * @agentic @benchmark/core
 */
export function useUnifiedInsuranceSearch(
  searchQuery: string,
  config: { enabled?: boolean; minChars?: number } = {},
): UseUnifiedInsuranceSearchResult {
  const medplum = useMedplum();
  const debouncedQuery = useDebounce(searchQuery, 300);
  const minChars = config.minChars ?? 2;

  const [data, setData] = useState<InsuranceSearchResultGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const isEnabled =
    (config.enabled ?? true) && debouncedQuery.trim().length >= minChars;

  useEffect(() => {
    if (!isEnabled) {
      queueMicrotask(() => {
        setData([]);
      });
      return;
    }

    const controller = new AbortController();

    const performSearch = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const trimmedQuery = debouncedQuery.trim();

        // Execute both searches in parallel
        const [planBundle, orgBundle] = await Promise.all([
          // Query for InsurancePlans with _include for owned-by Organization
          medplum.search("InsurancePlan", {
            status: "active",
            "name:contains": trimmedQuery,
            _include: "InsurancePlan:owned-by",
            _count: "50",
          }),
          // Query for Organizations with _revinclude for InsurancePlans
          medplum.search("Organization", {
            active: "true",
            type: "ins",
            "name:contains": trimmedQuery,
            _revinclude: "InsurancePlan:owned-by",
            _count: "50",
          }),
        ]);

        if (controller.signal.aborted) return;

        // Extract resources from both bundles
        const plansFromPlanQuery = extractFromBundle<InsurancePlan>(
          planBundle as Bundle,
          "InsurancePlan",
        );
        const orgsFromPlanQuery = extractFromBundle<Organization>(
          planBundle as Bundle,
          "Organization",
        );

        const orgsFromOrgQuery = extractFromBundle<Organization>(
          orgBundle as Bundle,
          "Organization",
        );
        const plansFromOrgQuery = extractFromBundle<InsurancePlan>(
          orgBundle as Bundle,
          "InsurancePlan",
        );

        // Merge and dedupe organizations
        const allOrgs = new Map<string, Organization>();
        [...orgsFromOrgQuery, ...orgsFromPlanQuery].forEach((org) => {
          if (org.id && !allOrgs.has(org.id)) {
            allOrgs.set(org.id, org);
          }
        });

        // Merge and dedupe plans
        const allPlans = new Map<string, InsurancePlan>();
        [...plansFromPlanQuery, ...plansFromOrgQuery].forEach((plan) => {
          if (plan.id && !allPlans.has(plan.id)) {
            allPlans.set(plan.id, plan);
          }
        });

        const grouped = groupByOrganization(
          Array.from(allOrgs.values()),
          Array.from(allPlans.values()),
        );

        if (!controller.signal.aborted) {
          setData(grouped);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    performSearch();

    return () => controller.abort();
  }, [medplum, debouncedQuery, isEnabled]);

  const clearData = useCallback(() => {
    setData([]);
  }, []);

  return {
    data,
    isLoading,
    error,
    clearData,
  };
}
